import { useState } from "react";
import { toast } from "sonner";
import { API } from "@/App";
import { 
  Loader2, Mail, User, Phone, Building, Play
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function LeadCaptureGate({ open, onClose, onDemoStart }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "demo_request",
          name: formData.name,
          email: formData.email,
          subject: `Demo Request from ${formData.company || formData.name}`,
          message: `
Demo Request Details:
---------------------
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || "Not provided"}
Company: ${formData.company || "Not provided"}

Additional Message:
${formData.message || "No additional message"}

Source: Watch Demo Button - Lead Capture Gate
          `.trim()
        })
      });

      if (response.ok) {
        toast.success("Thank you! Opening presentation...");
        // Close modal and open the 12-slide demo presentation
        onClose();
        if (onDemoStart) {
          onDemoStart();
        }
        setFormData({ name: "", email: "", phone: "", company: "", message: "" });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Lead capture error:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-lg p-0 overflow-hidden"
        style={{ 
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '1rem',
        }}
        data-testid="lead-capture-modal"
      >
        {/* Header with dark gradient background */}
        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          padding: '1.5rem',
        }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ color: '#ffffff', fontSize: '1.5rem', fontWeight: '700' }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Play className="w-6 h-6 text-white" />
              </div>
              Watch AskIsaac Demo
            </DialogTitle>
            <DialogDescription style={{ color: '#94a3b8', marginTop: '0.5rem', fontSize: '0.95rem' }}>
              Enter your details to access our platform demo and receive a copy of our one-pager.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Form Body - Light background with dark text */}
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem', backgroundColor: '#ffffff' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            {/* Name Field */}
            <div>
              <label style={{ 
                color: '#1e293b', 
                fontSize: '0.875rem', 
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem',
              }}>
                <User className="w-4 h-4" style={{ color: '#0891b2' }} />
                Your Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="lead-capture-name"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  borderRadius: '0.5rem',
                  color: '#1e293b',
                  fontSize: '0.95rem',
                  outline: 'none',
                }}
                onFocus={(e) => e.target.style.borderColor = '#0891b2'}
                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
              />
            </div>

            {/* Email Field */}
            <div>
              <label style={{ 
                color: '#1e293b', 
                fontSize: '0.875rem', 
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem',
              }}>
                <Mail className="w-4 h-4" style={{ color: '#0891b2' }} />
                Email Address <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="email"
                placeholder="john@hospital.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="lead-capture-email"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  borderRadius: '0.5rem',
                  color: '#1e293b',
                  fontSize: '0.95rem',
                  outline: 'none',
                }}
                onFocus={(e) => e.target.style.borderColor = '#0891b2'}
                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            {/* Phone Field */}
            <div>
              <label style={{ 
                color: '#1e293b', 
                fontSize: '0.875rem', 
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem',
              }}>
                <Phone className="w-4 h-4" style={{ color: '#0891b2' }} />
                Phone Number
              </label>
              <input
                type="tel"
                placeholder="(800) 988-3892"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="lead-capture-phone"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  borderRadius: '0.5rem',
                  color: '#1e293b',
                  fontSize: '0.95rem',
                  outline: 'none',
                }}
                onFocus={(e) => e.target.style.borderColor = '#0891b2'}
                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
              />
            </div>

            {/* Organization Field */}
            <div>
              <label style={{ 
                color: '#1e293b', 
                fontSize: '0.875rem', 
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem',
              }}>
                <Building className="w-4 h-4" style={{ color: '#0891b2' }} />
                Organization
              </label>
              <input
                type="text"
                placeholder="Memorial Hospital"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                data-testid="lead-capture-company"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  borderRadius: '0.5rem',
                  color: '#1e293b',
                  fontSize: '0.95rem',
                  outline: 'none',
                }}
                onFocus={(e) => e.target.style.borderColor = '#0891b2'}
                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
              />
            </div>
          </div>

          {/* Message Field */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              color: '#1e293b', 
              fontSize: '0.875rem', 
              fontWeight: '600',
              display: 'block',
              marginBottom: '0.5rem',
            }}>
              Additional Information <span style={{ color: '#64748b', fontWeight: '400' }}>(Optional)</span>
            </label>
            <textarea
              placeholder="Tell us about your facility or any specific interests..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              data-testid="lead-capture-message"
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                backgroundColor: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '0.5rem',
                color: '#1e293b',
                fontSize: '0.95rem',
                outline: 'none',
                resize: 'vertical',
                minHeight: '80px',
              }}
              onFocus={(e) => e.target.style.borderColor = '#0891b2'}
              onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
            />
          </div>

          {/* What happens next box */}
          <div style={{
            backgroundColor: '#f1f5f9',
            border: '1px solid #e2e8f0',
            borderRadius: '0.75rem',
            padding: '1rem',
            marginBottom: '1.25rem',
          }}>
            <p style={{ color: '#475569', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>
              What happens next:
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                width: '28px',
                height: '28px',
                backgroundColor: '#0891b2',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Play className="w-4 h-4" style={{ color: '#ffffff' }} />
              </div>
              <span style={{ color: '#334155', fontSize: '0.875rem' }}>Platform demo will start automatically</span>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            data-testid="lead-capture-submit"
            style={{
              width: '100%',
              padding: '1rem 1.5rem',
              fontSize: '1.1rem',
              fontWeight: '700',
              color: '#ffffff',
              background: 'linear-gradient(135deg, #0891b2, #2563eb)',
              border: 'none',
              borderRadius: '0.75rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              boxShadow: '0 4px 14px rgba(8, 145, 178, 0.3)',
              transition: 'all 0.2s ease',
            }}
            onMouseOver={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Watch Demo Now
              </>
            )}
          </button>

          {/* Footer text */}
          <p style={{ 
            fontSize: '0.75rem', 
            color: '#64748b', 
            textAlign: 'center',
            marginTop: '1rem',
            lineHeight: '1.5',
          }}>
            By submitting, you agree to receive communications from AskIsaac.
            <br />
            Questions? Contact us at{" "}
            <a href="mailto:info@sterilemate.com" style={{ color: '#0891b2', textDecoration: 'none' }}>
              info@sterilemate.com
            </a>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
